import React from "react";
import { Line } from "react-konva";
import { computeWallFloorPolygons } from "../../../../utils/wallFloorFill";
import { getAllWallSegments } from "../../../../utils/wallPathSegments";
import { useMapCanvasShapes } from "../../contexts/MapCanvasContext";
import { useMapEditorTool } from "../../contexts/MapEditorContext";

/**
 * "Auto Zone" tool overlay — detects closed rooms from the wall graph
 * (reusing the same wall-floor-fill face-detection already used to paint
 * white floor fills) and renders each as a dashed ghost polygon. Clicking
 * one materializes it into a real polygon zone; the click itself is
 * handled in useMapEditorHandlers.ts (reads the `roomIndex` attr set here
 * and recomputes the same polygons to look up its points).
 */
export const AutoZoneOverlay: React.FC = () => {
  const { activeTool } = useMapEditorTool();
  const { shapes } = useMapCanvasShapes();
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  const polygons = React.useMemo(() => {
    if (activeTool !== "auto-zone") return [];
    return computeWallFloorPolygons(getAllWallSegments(shapes));
  }, [activeTool, shapes]);

  if (activeTool !== "auto-zone") return null;

  return (
    <>
      {polygons.map((polygon, index) => (
        <Line
          // biome-ignore lint/suspicious/noArrayIndexKey: matches the click handler's re-derived index, both from the same deterministic computeWallFloorPolygons(shapes) call
          key={index}
          roomIndex={index}
          points={polygon.points.flatMap((p) => [p.x, p.y])}
          closed
          fill="#2563eb"
          opacity={hoveredIndex === index ? 0.32 : 0.18}
          stroke="#2563eb"
          strokeWidth={2}
          dash={[8, 4]}
          onMouseEnter={(event) => {
            event.target.getStage()!.container().style.cursor = "pointer";
            setHoveredIndex(index);
          }}
          onMouseLeave={(event) => {
            event.target.getStage()!.container().style.cursor = "crosshair";
            setHoveredIndex(null);
          }}
        />
      ))}
    </>
  );
};
